import React, { Fragment, memo, useCallback, useState, useRef } from 'react';
import classNames from 'classnames';
import { Form } from 'react-bootstrap';
import utils from '../../utils/utils';
import eyeIcon from '../../assets/images/eye-purple.svg';
import eyeOffIcon from '../../assets/images/eye-closed.svg';

import './SSNControl.scss';

const SSNControl = ({ ssn, name, setSsn, label, hasFocus, onFocus, customClass, isInvalid }) => {
	const inputRef = useRef(null);

	const [visible, setVisible] = useState(false);
	const [latestCharIndex, setLatestCharIndex] = useState(null);

	const toggleVisibility = () => setVisible(!visible);

	/* eslint-disable-next-line react-hooks/exhaustive-deps */
	const debounceLatestCharIndex = useCallback(utils.debounce(setLatestCharIndex, 2000), []);

	const getFormattedSSN = (old, _new) => {
		if (old.length === 11 && _new.length === 12) return old;

		let formattedNumber = _new.replace(/\D/g, '');
		if (formattedNumber.length > 9) {
			formattedNumber = formattedNumber.slice(formattedNumber.length - 10);
		}
		if (formattedNumber.length > 3) {
			formattedNumber = [formattedNumber.slice(0, 3), formattedNumber.slice(3)].join('-');
		}
		if (formattedNumber.length > 6) {
			formattedNumber = [formattedNumber.slice(0, 6), formattedNumber.slice(6)].join('-');
		}
		return formattedNumber;
	};

	const changeSSN = (e) => {
		let caretPosition = e.target.selectionStart;
		const newValue = e.target.value;

		const formatted = getFormattedSSN(ssn, newValue);
		if (formatted.length > ssn.length && (caretPosition === 4 || caretPosition === 7)) {
			caretPosition += 1;
		}
		/* set the index on the char to be shown if in hidden display of ssn */
		if (!visible) {
			if (newValue.length < ssn.length) {
				setLatestCharIndex(null);
			} else {
				setLatestCharIndex(caretPosition - 1);
				debounceLatestCharIndex(null);
			}
		}
		setSsn({ name, value: formatted });
		setTimeout(() => inputRef.current.setSelectionRange(caretPosition, caretPosition), 0);
	};

	const getMaskedSsn = (_ssn, index) => {
		const masked = _ssn.replace(/\d/g, '*');
		if (index !== null) {
			const ssnChars = masked.split('');
			ssnChars[index] = _ssn.charAt(index);
			return ssnChars.join('');
		}

		return masked;
	};

	const displayedSsn = visible ? ssn : getMaskedSsn(ssn, latestCharIndex);

	return (
		<>
			<div className="label">{label}</div>
			<div className={classNames('ssn-wrapper', customClass, { highlight: hasFocus, 'is-invalid': isInvalid })}>
				<div className="placeholder-mask">***-**-****</div>
				<div className="displayed-value-mask">{displayedSsn}</div>
				<input
					type="tel"
					autoComplete="off"
					maxLength={11}
					value={ssn}
					ref={inputRef}
					onChange={changeSSN}
					onFocus={onFocus}
					className={classNames({ 'is-invalid': isInvalid })}
				/>
				<Form.Control.Feedback type="invalid">{isInvalid}</Form.Control.Feedback>
				{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions */}
				{ssn && <img src={visible ? eyeIcon : eyeOffIcon} alt="" className="visibility" onClick={toggleVisibility} />}
			</div>
		</>
	);
};

export default memo(SSNControl);
